/**
 * /licenses — Customer's self-host license overview (OCP-116).
 *
 * Lists every license issued to this customer (active + revoked +
 * expired). Each card links to /licenses/:id for the manage flow:
 * download (.orbotolic re-mint), revoke, linked-instance list.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface License {
  id: string;
  display_name: string;
  tier: 'solo' | 'team' | 'business' | 'enterprise';
  status: 'active' | 'revoked' | 'expired';
  max_instances: number | null;
  features: string[];
  air_gapped: boolean;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  license_key_prefix: string;
  subscription_id: string | null;
}

/** Badge colours per status; labels live in `licenses:status.<key>` / `licenses:tier.<key>` (OCP-656). */
const STATUS_BADGE: Record<License['status'], string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  revoked: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  expired: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

export function LicensesPage() {
  const { t } = useTranslation('licenses');
  const [items, setItems] = useState<License[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<{ items: License[] }>('/v1/portal/licenses', {
          signal: ac.signal,
        });
        setItems(r.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('list.loadFailedHttp', { status: err.status }));
        else setError(t('list.loadFailed'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('list.title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('list.introBefore')} <code>.orbotolic</code> {t('list.introAfter')}
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('list.loading')}</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('list.emptyBefore')}{' '}
          <Link to="/products" className="text-brand-700 underline">
            {t('list.emptyLink')}
          </Link>
          .
        </div>
      )}

      <div className="space-y-3">
        {items?.map((l) => (
          <LicenseCard key={l.id} license={l} />
        ))}
      </div>
    </div>
  );
}

function LicenseCard({ license }: { license: License }) {
  const { t } = useTranslation('licenses');
  const badge = { label: t(`status.${license.status}`), cls: STATUS_BADGE[license.status] };
  return (
    <Link
      to={`/licenses/${license.id}`}
      data-testid={`license-card-${license.id}`}
      className={`block rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/30 ${
        license.status !== 'active' ? 'opacity-70' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{license.display_name}</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t(`tier.${license.tier}`)}
            {license.max_instances === null
              ? t('cap.unlimited')
              : t('cap.upTo', { count: license.max_instances })}
            {license.air_gapped ? t('cap.airGapped') : ''}
          </p>
          <p className="mt-1 font-mono text-xs text-gray-400">
            {t('list.key', { prefix: license.license_key_prefix })}
          </p>
        </div>
        <span
          data-testid={`license-status-${license.id}`}
          className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {t('list.issued', { date: formatDate(license.issued_at) })}
        {license.expires_at ? t('list.expires', { date: formatDate(license.expires_at) }) : t('list.perpetual')}
      </p>
    </Link>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}
